import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

export const DEMO_MODE_KEY = 'optoscale-demo-mode';

interface DemoContextValue {
    isDemoMode: boolean;
    toggleDemoMode: () => void;
}

const DemoContext = createContext<DemoContextValue | null>(null);

export function DemoProvider({ children }: { children: ReactNode }) {
    const queryClient = useQueryClient();
    const [isDemoMode, setIsDemoMode] = useState(() => {
        if (typeof window === 'undefined') return false;
        try {
            return localStorage.getItem(DEMO_MODE_KEY) === 'true';
        } catch {
            return false;
        }
    });

    useEffect(() => {
        if (typeof window !== 'undefined') {
            try {
                localStorage.setItem(DEMO_MODE_KEY, String(isDemoMode));
            } catch {
                // Ignore storage errors
            }
        }
    }, [isDemoMode]);

    const toggleDemoMode = () => {
        setIsDemoMode((prev) => {
            const newValue = !prev;
            // Invalidate all queries when demo mode changes to trigger refetch with new header
            queryClient.invalidateQueries();
            return newValue;
        });
    };

    return <DemoContext value={{ isDemoMode, toggleDemoMode }}>{children}</DemoContext>;
}

export function useDemoMode(): DemoContextValue {
    const context = useContext(DemoContext);
    if (!context) {
        throw new Error('useDemoMode must be used within DemoProvider');
    }
    return context;
}
